import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useCart } from "@/contexts/CartContext";
import { useCurrency } from "@/contexts/MarketContext";
import { supabase } from "@/integrations/supabase/client";
import { cmsApi } from "@/services/cmsApi";

interface BundleProduct {
  product_id: string;
  product?: {
    id: string;
    title: string;
    price: number;
    image_url: string | null;
    slug: string;
  };
}

interface Bundle {
  id: string;
  bundle_products: BundleProduct[];
  discount_percentage: number;
}

interface CartSidebarProps {
  open: boolean;
  onClose: () => void;
  productId?: string;
}

export const CartSidebar = ({ open, onClose, productId }: CartSidebarProps) => {
  const { items, removeFromCart, updateQuantity, itemCount, addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [loading, setLoading] = useState(false);

  console.log('CartSidebar render - open:', open, 'itemCount:', itemCount);

  useEffect(() => {
    if (open && productId) {
      fetchBundles();
    }
  }, [open, productId]);

  const fetchBundles = async () => {
    if (!productId) return;
    
    setLoading(true);
    try {
      // Fetch bundle structure from local DB (just IDs and discount)
      const { data, error } = await supabase
        .from('product_bundles')
        .select('id, discount_percentage, bundle_product_id')
        .eq('main_product_id', productId)
        .limit(3);

      if (error) throw error;
      
      if (!data || data.length === 0) {
        setBundles([]);
        return;
      }

      // Extract product IDs and fetch details from CMS
      const productIds = data.map(b => b.bundle_product_id);
      const { products } = await cmsApi.getProductsByIds(productIds);
      
      // Merge bundle data with product details
      const formattedBundles = data.map(bundle => {
        const product = products.find(p => p.id === bundle.bundle_product_id);
        return {
          id: bundle.id,
          discount_percentage: bundle.discount_percentage,
          bundle_products: [{
            product_id: bundle.bundle_product_id,
            product: product ? {
              id: product.id,
              title: product.title,
              price: product.price || 0,
              image_url: product.image_url,
              slug: product.slug
            } : undefined
          }]
        };
      }).filter(b => b.bundle_products[0]?.product); // Only include bundles with valid products
      
      setBundles(formattedBundles);
    } catch (error) {
      console.error('Error fetching bundles:', error);
      setBundles([]);
    } finally {
      setLoading(false);
    }
  };

  const subtotal = items.reduce((sum, item) => sum + ((item.product?.price || 0) * item.quantity), 0);

  const handleViewCart = () => {
    onClose();
    window.scrollTo(0, 0);
    navigate('/cart');
  };

  const handleCheckout = () => {
    onClose();
    window.scrollTo(0, 0);
    navigate('/checkout');
  };

  const handleAddBundle = async (productId: string) => {
    await addToCart(productId);
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0">
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            Cart ({itemCount})
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Your cart is empty
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <Card key={item.id} className="p-4">
                  <div className="flex gap-4">
                    {item.product?.image_url && (
                      <img
                        src={item.product.image_url}
                        alt={item.product.title}
                        className="w-20 h-20 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => {
                          if (item.product?.slug) {
                            onClose();
                            window.scrollTo(0, 0);
                            navigate(`/product/${item.product.slug}`);
                          }
                        }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 
                        className="font-medium truncate cursor-pointer hover:text-primary transition-colors"
                        onClick={() => {
                          if (item.product?.slug) {
                            onClose();
                            window.scrollTo(0, 0);
                            navigate(`/product/${item.product.slug}`);
                          }
                        }}
                      >
                        {item.product?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {formatPrice(item.product?.price || 0)}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="text-sm w-8 text-center">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 ml-auto"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}

              {/* Multi-Buy Discount Info - More discreet */}
              <div className="pt-4 border-t">
                <p className="text-xs text-muted-foreground text-center">
                  💰 Save up to 15% with multi-buy: 2 items (5%) • 3 items (10%) • 4+ items (15%)
                </p>
              </div>

              {/* Upsell Section */}
              {bundles.length > 0 && (
                <div className="mt-8 pt-8 border-t">
                  <h3 className="font-semibold mb-4">Frequently Bought Together</h3>
                  <div className="space-y-3">
                    {bundles.map((bundle) => {
                      const bundleProduct = bundle.bundle_products[0]?.product;
                      if (!bundleProduct) return null;

                      const discountedPrice = bundleProduct.price * (1 - bundle.discount_percentage / 100);

                      return (
                        <Card key={bundle.id} className="p-4">
                          <div className="flex gap-3 items-start">
                            {bundleProduct.image_url && (
                              <img
                                src={bundleProduct.image_url}
                                alt={bundleProduct.title}
                                className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => {
                                  onClose();
                                  window.scrollTo(0, 0);
                                  navigate(`/product/${bundleProduct.slug}`);
                                }}
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 
                                className="font-medium text-sm truncate cursor-pointer hover:text-primary transition-colors"
                                onClick={() => {
                                  onClose();
                                  window.scrollTo(0, 0);
                                  navigate(`/product/${bundleProduct.slug}`);
                                }}
                              >
                                {bundleProduct.title}
                              </h4>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-sm line-through text-muted-foreground">
                                  {formatPrice(bundleProduct.price)}
                                </span>
                                <span className="text-sm font-semibold text-primary">
                                  {formatPrice(discountedPrice)}
                                </span>
                                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                                  Save {bundle.discount_percentage}%
                                </span>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="mt-2 w-full"
                                onClick={() => handleAddBundle(bundle.bundle_products[0].product_id)}
                              >
                                Add to Cart
                              </Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t px-6 py-4 space-y-3 bg-muted/20">
            <div className="flex justify-between text-lg font-semibold">
              <span>Subtotal:</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="outline"
                size="lg"
                onClick={handleViewCart}
              >
                View Cart
              </Button>
              <Button 
                size="lg"
                onClick={handleCheckout}
              >
                Checkout
              </Button>
            </div>
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={onClose}
            >
              Continue Shopping
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
